import { ElementType, FunctionComponent, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemButton from '@mui/material/ListItemButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ToggleExpandButton } from '@cbridge-tools/button';
import { IFilterItem } from '../../block/FilterBlock/FilterBlock.interface';
import { FilterBlockItems } from '../FilterBlockItems/FilterBlockItems';
import { IFilterBlockItemBaseProps } from './FilterBlockItemBase.interface';
import { getListItemButtonStyle } from './FilterBlockItemBase.style';

const ItemContent = styled(Box)({
  flex: 1,
  minWidth: 0,
  marginTop: '2px',
  marginBottom: '2px',
  overflow: 'hidden',
});

const LabelTypography = styled(Typography)<{ component?: ElementType }>({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
});

const IconRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'center',
  minWidth: 0,
  // Only the label gives way when space runs out; the status icon keeps its size.
  '& > svg': {
    flexShrink: 0,
  },
}));

// The ellipsis has to sit on the element that holds the text itself: the parent only clips it
// (`overflow: hidden`), so without this a long label is cut mid-glyph instead of trailing off.
const LabelText = styled(Box)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const collectAllNestedValues = (items: IFilterItem[]): (string | number)[] => {
  const result: (string | number)[] = [];
  const collect = (itemList: IFilterItem[]) => {
    itemList.forEach((item) => {
      if (item.nested?.length > 0) {
        collect(item.nested);
      } else {
        result.push(item.value);
      }
    });
  };
  collect(items);
  return result;
};

export const FilterBlockItemBase: FunctionComponent<IFilterBlockItemBaseProps> = (props) => {
  const {
    item: { Icon, label, nested, value, unavailable },
    onClick,
    action,
    hoverAction,
    status,
    isSelected,
    depth = 0,
    isCheckboxMode,
    onToggleCheckbox,
    checked,
    isNullMarginLeft = false,
    count,
    depthPaddingMultiplier,
  } = props;

  const hasNestedItems = nested?.length > 0;

  const [expanded, setExpanded] = useState<boolean>(hasNestedItems);

  const listItemButtonSx = getListItemButtonStyle(
    depth,
    hasNestedItems,
    isNullMarginLeft,
    depthPaddingMultiplier,
  );

  const handleItemClick = () => {
    if (onClick) {
      onClick();
    } else if (isCheckboxMode && onToggleCheckbox) {
      if (hasNestedItems) {
        onToggleCheckbox(collectAllNestedValues(nested));
      } else {
        onToggleCheckbox([value]);
      }
    }
  };

  return (
    <>
      <ListItem
        sx={{ p: 0, overflowX: 'hidden' }}
        secondaryAction={action}
      >
        <ListItemButton
          selected={isSelected}
          onClick={handleItemClick}
          sx={listItemButtonSx}
        >
          {hasNestedItems && (
            <ToggleExpandButton
              isExpanded={expanded}
              onChange={setExpanded}
              sx={{ p: isCheckboxMode ? 0 : 0.25 }}
            />
          )}
          {hasNestedItems && isCheckboxMode && (
            <Checkbox
              onChange={() => onToggleCheckbox(collectAllNestedValues(nested))}
              checked={checked}
              sx={{ padding: 0, marginLeft: 0.5, marginRight: 1 }}
            />
          )}
          {!hasNestedItems && isCheckboxMode && (
            <Checkbox
              onChange={() => onToggleCheckbox([value])}
              checked={checked}
              sx={{ padding: 0, marginRight: 1 }}
            />
          )}
          <ItemContent>
            <Tooltip
              title={label}
              arrow={true}
              disableInteractive={true}
              slotProps={{
                popper: {
                  sx: {
                    '&[data-popper-reference-hidden]': {
                      display: 'none',
                    },
                  },
                },
              }}
            >
              <LabelTypography
                component={'span'}
                noWrap={true}
                color={unavailable === true ? 'error' : 'inherit'}>
                <IconRow>
                  {Icon && <Icon size={'small'} />}
                  <LabelText>{label}</LabelText>
                </IconRow>
              </LabelTypography>
            </Tooltip>
          </ItemContent>
          {status}
          {hoverAction}
          {count !== undefined && (
            <Typography sx={{
              ml: 1,
            }}>
              {count}
            </Typography>
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} timeout={'auto'} unmountOnExit={true}>
        <FilterBlockItems
          depth={depth + 1}
          items={nested}
        />
      </Collapse>
    </>
  );
};
